import { useMemo } from 'react';
import { FormField } from 'src/components';
import {
  ActionType,
  BehaviorConfigOption,
  ConfigActionType,
  ConfigDataAction,
  MacroActionType,
  SelectorValue,
  useMacroConfig,
  useSelectedPartner,
} from 'src/graphql';
import {
  getSelectorOptions,
  mapAction,
  UseQueryOptions,
  useQueryOptions,
} from 'src/pages/Settings/rules/use-rules-engine';

const SUPPORTED_ACTION_TYPES = new Set([
  ActionType.Data,
  ActionType.ManageConversationLabels,
  ActionType.ManageSubAssignees,
  ActionType.Signal,
]);

type CustomActionField = FormField & {
  fieldOptions?: BehaviorConfigOption[];
};

interface MacroConfigActionFields {
  [ActionType.Data]: SelectorValue[];
  [ActionType.Signal]: CustomActionField[];
  [ActionType.ManageConversationLabels]: CustomActionField[];
  [ActionType.ManageSubAssignees]: CustomActionField[];
}
export interface MacrosEngineConfig {
  actionTypes: ConfigActionType<MacroActionType>[];
  actions: MacroConfigActionFields;
  timezone?: string;
  queryOptions?: UseQueryOptions;
}

const DEFAULT_CONFIG: MacrosEngineConfig = {
  actionTypes: [],
  actions: {} as MacroConfigActionFields,
  timezone: undefined,
};

export function useMacrosEngine(): MacrosEngineConfig {
  const { selectedPartner } = useSelectedPartner();

  const { macroConfig } = useMacroConfig();
  const queryOptions = useQueryOptions(macroConfig);

  const { actionTypes, actions } = useMemo(() => {
    if (!macroConfig) {
      return {
        actionTypes: [] as ConfigActionType<MacroActionType>[],
        actions: {} as MacroConfigActionFields,
      };
    }

    const actionTypes = macroConfig.actionTypes.filter((action) => {
      return SUPPORTED_ACTION_TYPES.has(action.value);
    });

    const actions = [...SUPPORTED_ACTION_TYPES.values()].reduce((acc, actionType) => {
      const actions = macroConfig.actions[actionType as MacroActionType];

      if (!actions) return acc;

      if (actionType === ActionType.Data) {
        return {
          ...acc,
          [actionType]: (actions as ConfigDataAction[]).map((item) => ({
            ...mapAction(item, actionType),
            ...getSelectorOptions(item, queryOptions),
          })) as SelectorValue[],
        };
      }

      const actionAttributes = Array.isArray(actions)
        ? actions
        : [actions].flatMap((item) => item.attributes);

      return {
        ...acc,
        [actionType]: actionAttributes.map((item) => {
          const queryData = getSelectorOptions(item, queryOptions);
          const mappedAction = mapAction(item, actionType);
          let options = queryData.fieldOptions;

          if ('options' in mappedAction) {
            options = mappedAction.options as BehaviorConfigOption[];
          }
          if ('fieldOptions' in mappedAction) {
            options = mappedAction.fieldOptions;
          }

          return {
            ...mappedAction,
            options,
            fieldOptions: options,
          };
        }),
      };
    }, {} as MacroConfigActionFields);

    return { actionTypes, actions };
  }, [macroConfig, queryOptions]);

  const config = useMemo(
    () => ({
      actionTypes,
      actions,
      timezone: selectedPartner?.timeZone ? selectedPartner.timeZone : undefined,
      queryOptions,
    }),
    [actionTypes, actions, selectedPartner?.timeZone, queryOptions],
  );

  if (!macroConfig) return DEFAULT_CONFIG;

  return config;
}
